import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  Calendar,
  Book,
  Mic,
  Image as ImageIcon,
  Loader2,
  CheckCircle2,
  AlertCircle
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { AdminUser } from '@/lib/authService'
import { useSubscription } from '@/hooks/useSubscription'
import { FeatureRestricted } from '@/components/subscription/FeatureRestricted'

interface DevotionsProps {
  user: AdminUser | null
}

interface Devotion {
  id: string
  title: string
  content: string
  scripture_reference: ScriptureReference[]
  scheduled_date: string
  is_published: boolean
  audio_url?: string
  image_url?: string
  reflection_questions?: string[]
  views_count: number
  author_name?: string
  created_at: string
}

interface ScriptureReference {
  book: string
  chapter: number
  verse_start?: number
  verse_end?: number
}

export default function Devotions({ user }: DevotionsProps) {
  const { features, limits, loading: subscriptionLoading } = useSubscription(user?.churchId)
  const [devotions, setDevotions] = useState<Devotion[]>([])
  const [loading, setLoading] = useState(true)
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  // Form state
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [scheduledDate, setScheduledDate] = useState('')
  const [scriptureBook, setScriptureBook] = useState('')
  const [scriptureChapter, setScriptureChapter] = useState('')
  const [scriptureVerseStart, setScriptureVerseStart] = useState('')
  const [scriptureVerseEnd, setScriptureVerseEnd] = useState('')
  const [reflectionQuestion1, setReflectionQuestion1] = useState('')
  const [reflectionQuestion2, setReflectionQuestion2] = useState('')
  const [reflectionQuestion3, setReflectionQuestion3] = useState('')
  const [isPublished, setIsPublished] = useState(false)

  useEffect(() => {
    loadDevotions()
  }, [])

  async function loadDevotions() {
    try {
      setLoading(true)

      const { data: { user: authUser } } = await supabase.auth.getUser()
      if (!authUser) return

      const { data: profile } = await supabase
        .from('profiles')
        .select('church_id')
        .eq('id', authUser.id)
        .single()

      if (!profile?.church_id) return

      const { data, error } = await supabase
        .from('devotions')
        .select(`
          *,
          author:profiles(first_name, last_name)
        `)
        .eq('church_id', profile.church_id)
        .order('scheduled_date', { ascending: false })

      if (error) throw error

      const devotionsWithAuthor = data?.map(d => ({
        ...d,
        author_name: d.author ? `${d.author.first_name} ${d.author.last_name}` : 'Unknown'
      })) || []

      setDevotions(devotionsWithAuthor)
    } catch (error) {
      console.error('Error loading devotions:', error)
    } finally {
      setLoading(false)
    }
  }

  async function handleCreateDevotion(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)

    try {
      const { data: { user: authUser } } = await supabase.auth.getUser()
      if (!authUser) return

      const { data: profile } = await supabase
        .from('profiles')
        .select('church_id')
        .eq('id', authUser.id)
        .single()

      if (!profile?.church_id) {
        alert('No church ID found')
        return
      }

      // Build scripture reference
      const scriptureReference: ScriptureReference[] = scriptureBook && scriptureChapter ? [{
        book: scriptureBook,
        chapter: parseInt(scriptureChapter),
        verse_start: scriptureVerseStart ? parseInt(scriptureVerseStart) : undefined,
        verse_end: scriptureVerseEnd ? parseInt(scriptureVerseEnd) : undefined
      }] : []

      // Build reflection questions
      const reflectionQuestions = [
        reflectionQuestion1,
        reflectionQuestion2,
        reflectionQuestion3
      ].filter(q => q.trim())

      const { error } = await supabase
        .from('devotions')
        .insert({
          church_id: profile.church_id,
          author_id: authUser.id,
          title,
          content,
          scripture_reference: scriptureReference,
          scheduled_date: scheduledDate,
          is_published: isPublished,
          reflection_questions: reflectionQuestions.length > 0 ? reflectionQuestions : null
        })

      if (error) throw error

      // Reset form
      resetForm()
      setIsAddDialogOpen(false)
      loadDevotions()
    } catch (error: any) {
      console.error('Error creating devotion:', error)
      alert(error.message || 'Failed to create devotion')
    } finally {
      setSaving(false)
    }
  }

  function resetForm() {
    setTitle('')
    setContent('')
    setScheduledDate('')
    setScriptureBook('')
    setScriptureChapter('')
    setScriptureVerseStart('')
    setScriptureVerseEnd('')
    setReflectionQuestion1('')
    setReflectionQuestion2('')
    setReflectionQuestion3('')
    setIsPublished(false)
  }

  async function togglePublish(devotion: Devotion) {
    try {
      const { error } = await supabase
        .from('devotions')
        .update({ is_published: !devotion.is_published })
        .eq('id', devotion.id)

      if (error) throw error
      loadDevotions()
    } catch (error) {
      console.error('Error toggling publish:', error)
      alert('Failed to update devotion status')
    }
  }

  async function deleteDevotion(id: string) {
    if (!confirm('Are you sure you want to delete this devotion?')) return

    try {
      const { error } = await supabase
        .from('devotions')
        .delete()
        .eq('id', id)

      if (error) throw error
      loadDevotions()
    } catch (error) {
      console.error('Error deleting devotion:', error)
      alert('Failed to delete devotion')
    }
  }

  const filteredDevotions = devotions.filter(d =>
    d.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.content.toLowerCase().includes(searchTerm.toLowerCase())
  )

  function formatScripture(refs: ScriptureReference[]): string {
    if (!refs || refs.length === 0) return 'No scripture'
    const ref = refs[0]
    let text = `${ref.book} ${ref.chapter}`
    if (ref.verse_start) {
      text += `:${ref.verse_start}`
      if (ref.verse_end && ref.verse_end !== ref.verse_start) {
        text += `-${ref.verse_end}`
      }
    }
    return text
  }

  if (loading || subscriptionLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  if (!features.advancedMinistry) {
    return (
      <FeatureRestricted
        featureName="Devotions Management"
        description="Create and schedule daily devotions with scripture references for your church members."
        currentPlan={limits?.plan || 'starter'}
        requiredPlan="growth"
        icon={<Book className="h-10 w-10 text-white" />}
      />
    )
  }

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Daily Devotions</h1>
          <p className="text-muted-foreground">Create and manage devotional content for your church</p>
        </div>

        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Create Devotion
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-900">
            <form onSubmit={handleCreateDevotion}>
              <DialogHeader>
                <DialogTitle>Create Daily Devotion</DialogTitle>
                <DialogDescription>
                  Write a devotional message for your church members
                </DialogDescription>
              </DialogHeader>

              <div className="grid gap-4 py-4">
                {/* Title */}
                <div className="space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Walking in Faith"
                    required
                  />
                </div>

                {/* Scheduled Date */}
                <div className="space-y-2">
                  <Label htmlFor="date">Scheduled Date *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    required
                  />
                </div>

                {/* Scripture Reference */}
                <div className="space-y-2">
                  <Label>Scripture Reference *</Label>
                  <div className="grid grid-cols-4 gap-2">
                    <Input
                      placeholder="Book (e.g., John)"
                      value={scriptureBook}
                      onChange={(e) => setScriptureBook(e.target.value)}
                      required
                    />
                    <Input
                      type="number"
                      placeholder="Chapter"
                      value={scriptureChapter}
                      onChange={(e) => setScriptureChapter(e.target.value)}
                      required
                    />
                    <Input
                      type="number"
                      placeholder="Verse start"
                      value={scriptureVerseStart}
                      onChange={(e) => setScriptureVerseStart(e.target.value)}
                    />
                    <Input
                      type="number"
                      placeholder="Verse end"
                      value={scriptureVerseEnd}
                      onChange={(e) => setScriptureVerseEnd(e.target.value)}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Example: John 3:16 or John 3:16-17
                  </p>
                </div>

                {/* Content */}
                <div className="space-y-2">
                  <Label htmlFor="content">Devotional Content *</Label>
                  <Textarea
                    id="content"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Write your devotional message here..."
                    rows={8}
                    required
                  />
                </div>

                {/* Reflection Questions */}
                <div className="space-y-2">
                  <Label>Reflection Questions (Optional)</Label>
                  <Input
                    placeholder="Question 1"
                    value={reflectionQuestion1}
                    onChange={(e) => setReflectionQuestion1(e.target.value)}
                  />
                  <Input
                    placeholder="Question 2"
                    value={reflectionQuestion2}
                    onChange={(e) => setReflectionQuestion2(e.target.value)}
                  />
                  <Input
                    placeholder="Question 3"
                    value={reflectionQuestion3}
                    onChange={(e) => setReflectionQuestion3(e.target.value)}
                  />
                </div>

                {/* Publish Status */}
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="publish"
                    checked={isPublished}
                    onChange={(e) => setIsPublished(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <Label htmlFor="publish" className="font-normal cursor-pointer">
                    Publish immediately (members will see this devotion on the scheduled date)
                  </Label>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Devotion'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Devotions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{devotions.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Published</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {devotions.filter(d => d.is_published).length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Drafts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
              {devotions.filter(d => !d.is_published).length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Views</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {devotions.reduce((sum, d) => sum + d.views_count, 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Devotions Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Devotions</CardTitle>
          <CardDescription>Manage your church's devotional content</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <Input
              placeholder="Search devotions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-sm"
            />
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Scripture</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Author</TableHead>
                <TableHead>Views</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDevotions.length > 0 ? (
                filteredDevotions.map((devotion) => (
                  <TableRow key={devotion.id}>
                    <TableCell className="font-medium">{devotion.title}</TableCell>
                    <TableCell>
                      <div className="flex items-center text-sm">
                        <Book className="mr-2 h-3 w-3" />
                        {formatScripture(devotion.scripture_reference)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center text-sm">
                        <Calendar className="mr-2 h-3 w-3" />
                        {new Date(devotion.scheduled_date).toLocaleDateString()}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{devotion.author_name}</TableCell>
                    <TableCell>
                      <div className="flex items-center text-sm">
                        <Eye className="mr-2 h-3 w-3" />
                        {devotion.views_count}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={devotion.is_published ? 'default' : 'secondary'}>
                        {devotion.is_published ? (
                          <>
                            <CheckCircle2 className="mr-1 h-3 w-3" />
                            Published
                          </>
                        ) : (
                          <>
                            <AlertCircle className="mr-1 h-3 w-3" />
                            Draft
                          </>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => togglePublish(devotion)}
                          title={devotion.is_published ? 'Unpublish' : 'Publish'}
                        >
                          {devotion.is_published ? (
                            <AlertCircle className="h-4 w-4" />
                          ) : (
                            <CheckCircle2 className="h-4 w-4" />
                          )}
                        </Button>
                        <Button variant="ghost" size="sm" title="Edit">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteDevotion(devotion.id)}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    {searchTerm ? 'No devotions found matching your search' : 'No devotions yet. Create your first devotion!'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}
